// Import necessary modules
import { supabase } from './supabaseClient.js';
import { showStatus as utilShowStatus, formatCurrency } from './utils.js';
import { UserManagement } from './userManagement.js';
import { RevenueTypesManagement } from './revenueTypesManagement.js';
import { DrugsManagement } from './drugsManagement.js';
import { ReceiptManagement } from './receiptManagement.js';
import { Reporting } from './reporting.js';
import { CashierATransactions } from './cashierATransactions.js';
import { CashierBTransactions } from './cashierBTransactions.js';
import { SyncService } from './sync.js';

// The three engine-facing admin screens. They are initialised HERE, from the one
// role-aware module list, and not from inside another screen's `init()`:
// `DeviceProvisioning.init()` used to be called from `receiptManagement.js` as a
// stopgap, which made "does this device have an identity?" depend on whether the
// receipts screen happened to be wired up.
import { DeviceProvisioning } from './deviceManagement.js';
import { ConflictQueue } from './conflictQueue.js';
import { DayClose } from './dayClose.js';

// --- The money engine ---------------------------------------------------------
//
// There is NO `new Dexie(...)` in this file any more. `app.js`, `sync.js`,
// `cashierATransactions.js` and `cashierBTransactions.js` each declared
// `db.version(1)` on the SAME database name, and `sync.js` used a different
// primary key for `transactions`. Dexie does not error on that: after the first
// logout the store was recreated with keyPath `id` and no auto-increment, and
// every offline save threw `DataError` until the user cleared browser storage
// (audit C5). One schema module, one version chain, one handle.
import { db } from './db/schema.js';
import { getAllEvents, canLogout, performSafeLogout, pendingSummary } from './db/index.js';
import { bootEngine, getEngine, shutdownEngine } from './ui/engine-boot.js';
import { messageForError, pendingInTotalNotice } from './ui/engine-messages.js';
import { businessDateOf, foldMoney, foldStock, formatReceiptNo } from './money/index.js';

/** Low-stock threshold, in units. Unchanged from the pre-rewrite dashboard. */
const LOW_STOCK_THRESHOLD = 20;

/**
 * Today's business date (WAT, fixed +01:00 — invariant 9).
 *
 * `new Date().setHours(0,0,0,0)` is the local-machine day and is exactly how the
 * daily report lost an hour (audit H1): a start bound parsed as UTC midnight
 * against an end bound in local time. The fold and the report now derive the same
 * date from the same function.
 * @returns {string} `YYYY-MM-DD`
 */
function todayBusinessDate() {
    return businessDateOf(new Date().toISOString());
}

/**
 * Every ledger event this device knows: its own writes (synced or not) plus
 * everything the pull brought back.
 *
 * This is the input for EVERY money projection on this screen, which is what makes
 * audit H2 impossible to reintroduce — the dashboard, the chart and the reports
 * fold the same events with the same function, so they cannot disagree about the
 * same day. It also means queued offline money is INCLUDED by construction
 * (audit H6): a local mirror row exists the instant the cashier confirms.
 * @returns {Promise<object[]>}
 */
async function allLedgerEvents() {
    try {
        return await getAllEvents(db);
    } catch (error) {
        console.error('Lecture du registre local impossible:', error);
        return [];
    }
}

/**
 * Day-of-week (0 = Sunday) of a WAT business date, computed in UTC so the answer
 * does not depend on the machine's timezone.
 * @param {string} dateStr `YYYY-MM-DD`
 * @returns {number}
 */
function weekdayOf(dateStr) {
    const [y, m, d] = dateStr.split('-').map(Number);
    return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
}

/**
 * Shift a `YYYY-MM-DD` business date by whole days.
 * @param {string} dateStr
 * @param {number} days
 * @returns {string}
 */
function shiftBusinessDate(dateStr, days) {
    const [y, m, d] = dateStr.split('-').map(Number);
    const shifted = new Date(Date.UTC(y, m - 1, d + days));
    const pad = (n, w) => String(n).padStart(w, '0');
    return `${pad(shifted.getUTCFullYear(), 4)}-${pad(shifted.getUTCMonth() + 1, 2)}-${pad(shifted.getUTCDate(), 2)}`;
}

// --- Display helpers for the live dashboard feeds -----------------------------
//
// These name what an event IS. They never decide what it is WORTH: every figure
// on the dashboard still comes out of `foldMoney`/`foldStock` over the same
// events (audit H2). A label that carried its own arithmetic would be a third
// opinion about the same money.

/** @type {Object<string,string>} */
const KIND_LABELS = {
    sale: 'Vente',
    void: 'Annulation',
    discount: 'Remise',
    restock: 'Réapprovisionnement',
    stock_adjustment: 'Ajustement de stock'
};

/** @type {Object<string,string>} */
const CHANNEL_LABELS = { regie: 'Régie', pharmacy: 'Pharmacie' };

/** The channels a row may claim. Anything else renders unlabelled, never guessed. */
const CHANNELS_WITH_LABEL = new Set(['regie', 'pharmacy']);

/** @type {Object<string,string>} */
const ROLE_LABELS = {
    admin: 'Administrateur',
    cashier_a: 'Caissier A — Services',
    cashier_b: 'Caissier B — Pharmacie'
};

/**
 * Sort key for the activity feed, in milliseconds.
 *
 * `device_time` is the moment the cashier confirmed, which is what a human means
 * by "recent". It can be wrong — an unset device clock is a real condition here —
 * so a missing or unparseable one falls back to noon on the business date rather
 * than to 0, which would bury a today's sale at the bottom of the list.
 * @param {object} event
 * @returns {number}
 */
function eventInstant(event) {
    const stamped = Date.parse(event && event.device_time);
    if (Number.isFinite(stamped)) return stamped;
    const fromDay = Date.parse(`${(event && event.business_date) || ''}T12:00:00Z`);
    return Number.isFinite(fromDay) ? fromDay : 0;
}

/**
 * What was sold, in a few words: the item names an event carries, else the
 * patient, else the channel. Returns `null` rather than inventing a description.
 * @param {object} event
 * @returns {string|null}
 */
function describeItems(event) {
    const items = Array.isArray(event && event.items) ? event.items : [];
    const names = items
        .map(line => (line && typeof line.name === 'string' ? line.name.trim() : ''))
        .filter(Boolean);
    if (names.length === 1) return names[0];
    if (names.length > 1) return `${names[0]} + ${names.length - 1} autre(s)`;
    if (typeof event.patient_name === 'string' && event.patient_name.trim()) {
        return event.patient_name.trim();
    }
    return null;
}

/**
 * One ledger event, shaped for the « Transactions Récentes » feed.
 *
 * `pending` comes from `synced !== 1` — the same 0/1 flag the outbox drain uses,
 * not a second status column (spec §7: state is table membership).
 * @param {object} event
 * @returns {object}
 */
function toActivityRow(event, describedSale = new Map()) {
    const kind = typeof event.kind === 'string' ? event.kind : 'unknown';
    const amount = Number.isInteger(event.amount) ? event.amount : 0;
    const channel = CHANNELS_WITH_LABEL.has(event.channel) ? event.channel : null;
    const isCorrection = kind === 'void' || kind === 'discount';

    // A CORRECTION CARRIES NO ITEMS OF ITS OWN. It names the sale it cancels, so
    // the description is looked up on `reverses_id`. Without that lookup the
    // description fell back to the kind, the renderer prefixed the kind again,
    // and the feed read « Annulation — Annulation » — a row that says its type
    // twice and its subject never.
    const description = isCorrection
        ? (describedSale.get(event.reverses_id) || describeItems(event))
        : describeItems(event);

    return {
        id: event.id,
        kind,
        kindLabel: KIND_LABELS[kind] || 'Événement',
        channel,
        channelLabel: channel ? CHANNEL_LABELS[channel] : null,
        amount,
        // A void or a discount REMOVES cash. The feed shows the sign because a
        // list where an annulment looks like a sale is how a reversal gets read
        // as revenue twice.
        signed: isCorrection ? -amount : amount,
        // May be NULL. The renderer falls back to `kindLabel`; it must not be
        // pre-filled with the kind here, or the two concatenate into a stutter.
        label: description || (channel ? CHANNEL_LABELS[channel] : null),
        at: typeof event.device_time === 'string' ? event.device_time : null,
        businessDate: typeof event.business_date === 'string' ? event.business_date : null,
        receiptNo: Number.isInteger(event.receipt_no) ? formatReceiptNo(event.receipt_no) : null,
        pending: event.synced !== 1
    };
}

/**
 * Yesterday-versus-today for a tile, as a fact rather than as decoration.
 *
 * The dashboard used to print « Augmenté depuis hier » with an up-arrow under
 * every tile, unconditionally — on a day with no sales at all. A trend line that
 * cannot say "down" says nothing, and a hospital reading it during a slow week
 * would be told business is up while the drawer is empty.
 * @param {number} current
 * @param {number} previous
 * @param {(n:number)=>string} render How to write the two figures.
 * @param {string} sinceLabel e.g. `'depuis hier'`.
 * @returns {{direction:'up'|'down'|'flat', change:number, label:string}}
 */
function trendBetween(current, previous, render, sinceLabel) {
    const change = current - previous;
    if (change === 0) {
        return {
            direction: 'flat',
            change: 0,
            label: previous === 0 ? `Aucun mouvement, ${sinceLabel}` : `Inchangé ${sinceLabel}`
        };
    }
    return {
        direction: change > 0 ? 'up' : 'down',
        change,
        label: `${change > 0 ? '+' : '−'}${render(Math.abs(change))} ${sinceLabel}`
    };
}

/** First day of the month containing `dateStr`. @param {string} dateStr */
function startOfMonth(dateStr) {
    return `${dateStr.slice(0, 7)}-01`;
}

/** Short French month names, index 0 = janvier. */
const MONTH_LABELS = [
    'janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin',
    'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'
];

/** `YYYY-MM-DD` → epoch ms at UTC midnight. @param {string} dateStr */
function dateToUtcMs(dateStr) {
    const [y, m, d] = dateStr.split('-').map(Number);
    return Date.UTC(y, m - 1, d);
}

/** Whether a string is a well-formed calendar date, not merely date-shaped. */
function isBusinessDateString(value) {
    if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
    const ms = dateToUtcMs(value);
    if (Number.isNaN(ms)) return false;
    // `2026-02-31` parses without complaint and silently becomes 3 March. A range
    // bound that quietly moves is a report whose title disagrees with its contents.
    const [y, m, d] = value.split('-').map(Number);
    const back = new Date(ms);
    return back.getUTCFullYear() === y && back.getUTCMonth() + 1 === m && back.getUTCDate() === d;
}

/** Whole days from `from` to `to`, both inclusive. */
function daysInclusive(from, to) {
    return Math.round((dateToUtcMs(to) - dateToUtcMs(from)) / 86400000) + 1;
}

/** A range longer than this is refused rather than rendered as a forest of bars. */
const MAX_RANGE_DAYS = 3660;

/** How the caption names each bar, so a weekly bar is never read as a daily one. */
const GRANULARITY_NOUN = { day: 'jour', week: 'semaine', month: 'mois' };

/**
 * `2026-01-01` → `01/01/2026`, for prose read by francophone staff.
 *
 * Only ever for DISPLAY. Every bound, bucket and comparison in this file stays in
 * `YYYY-MM-DD`, which sorts lexicographically and is the one format the ledger,
 * the folds and the server all agree on.
 * @param {string} dateStr
 */
function frenchDate(dateStr) {
    return `${dateStr.slice(8, 10)}/${dateStr.slice(5, 7)}/${dateStr.slice(0, 4)}`;
}

/**
 * Choose the bar width for an arbitrary range.
 *
 * A chart is only honest if it can be read: 400 daily bars across a year is a
 * smear, and a reader who cannot resolve one bar from the next cannot check any
 * figure against a drawer. The buckets widen with the span — days, then weeks,
 * then months — and the caption always names which, so nobody mistakes a weekly
 * bar for a daily one.
 *
 * Weeks are Sunday-first, matching the « Semaine » view, so the same day never
 * lands in two differently-shaped buckets depending on which button was pressed.
 *
 * @param {string} from `YYYY-MM-DD`
 * @param {string} to `YYYY-MM-DD`
 * @returns {{granularity:'day'|'week'|'month', buckets:{label:string, dates:string[]}[]}}
 */
function bucketsForRange(from, to) {
    const span = daysInclusive(from, to);
    const allDates = Array.from({ length: span }, (_, i) => shiftBusinessDate(from, i));

    if (span <= 45) {
        return {
            granularity: 'day',
            buckets: allDates.map(d => ({ label: `${d.slice(8, 10)}/${d.slice(5, 7)}`, dates: [d] }))
        };
    }

    if (span <= 182) {
        const buckets = [];
        let cursor = shiftBusinessDate(from, -weekdayOf(from));
        while (cursor <= to) {
            const week = Array.from({ length: 7 }, (_, i) => shiftBusinessDate(cursor, i))
                .filter(d => d >= from && d <= to);
            if (week.length > 0) {
                buckets.push({ label: `${week[0].slice(8, 10)}/${week[0].slice(5, 7)}`, dates: week });
            }
            cursor = shiftBusinessDate(cursor, 7);
        }
        return { granularity: 'week', buckets };
    }

    const buckets = [];
    let cursor = startOfMonth(from);
    while (cursor <= to) {
        const prefix = cursor.slice(0, 7);
        const month = allDates.filter(d => d.startsWith(prefix));
        if (month.length > 0) {
            buckets.push({
                label: `${MONTH_LABELS[Number(prefix.slice(5, 7)) - 1]} ${prefix.slice(2, 4)}`,
                dates: month
            });
        }
        cursor = shiftBusinessDate(cursor, daysInMonth(cursor));
    }
    return { granularity: 'month', buckets };
}

/** Days in the month containing `dateStr`. @param {string} dateStr */
function daysInMonth(dateStr) {
    const [y, m] = dateStr.split('-').map(Number);
    return new Date(Date.UTC(y, m, 0)).getUTCDate();
}

/**
 * Group events by business date into a prepared map, so a caller folding N days
 * walks the ledger once instead of N times. 
 * @param {object[]} events
 * @param {string[]} dates
 * @returns {Map<string, object[]>}
 */
function bucketByDate(events, dates) {
    const byDate = new Map(dates.map(d => [d, []]));
    for (const event of events) {
        const date = event && event.business_date;
        if (date && byDate.has(date)) byDate.get(date).push(event);
    }
    return byDate;
}

// --- Global State Management ---
const AppState = {
    initialized: false,
    elements: {},
    realtimeChannels: [],
    modulesInitialized: {
        UserManagement: false,
        RevenueTypesManagement: false,
        DrugsManagement: false,
        ReceiptManagement: false,
        Reporting: false,
        CashierATransactions: false,
        CashierBTransactions: false,
        DeviceProvisioning: false,
        ConflictQueue: false,
        DayClose: false
    },
    currentUser: null,
    currentRole: null
};

// Helper function to safely get elements
function getElement(id) {
    if (!AppState.elements[id]) {
        AppState.elements[id] = document.getElementById(id);
    }
    return AppState.elements[id];
}

// Enhanced showStatus that uses UI.js if available, falls back to util
function showStatus(message, isError = false) {
    if (window.UI && window.UI.showStatus) {
        window.UI.showStatus(message, isError);
    } else {
        utilShowStatus(message, isError);
    }
}

// --- Data Fetching and Display Functions ---

/**
 * Fetches revenue types from Supabase and IndexedDB, then displays them.
 */
async function fetchAndDisplayRevenueTypes() {
    const revenueTypesUl = getElement('revenue-types-ul');
    if (!revenueTypesUl) {
        return;
    }

    revenueTypesUl.innerHTML = '<li class="text-muted">Chargement des services...</li>';
    let revenueTypes = [];

    try {
        const { data, error } = await supabase.from('revenue_types').select('*').order('name', { ascending: true });
        if (error) throw error;
        revenueTypes = data;
        // Use bulkPut to handle updates
        await db.revenueTypes.bulkPut(revenueTypes);
    } catch (networkError) {
        revenueTypes = await db.revenueTypes.toArray();
        if (revenueTypes.length > 0) showStatus('Mode hors ligne avec données en cache.', false);
    }

    revenueTypesUl.innerHTML = '';
    if (revenueTypes.length > 0) {
        revenueTypes.forEach(type => {
            const li = document.createElement('li');
            li.className = 'flex justify-between items-center py-2 px-3 rounded-lg hover:bg-gray-50 transition-colors';
            // textContent, not innerHTML: a service name is admin-supplied text and
            // this list renders in the admin's own session (audit M7).
            const name = document.createElement('span');
            name.className = 'text-gray-700';
            name.textContent = type.name;
            const price = document.createElement('span');
            price.className = 'font-semibold';
            price.style.color = 'var(--primary-700)';
            price.textContent = formatCurrency(type.amount);
            li.appendChild(name);
            li.appendChild(price);
            revenueTypesUl.appendChild(li);
        });
    } else {
        revenueTypesUl.innerHTML = '<li class="text-muted">Aucun service trouvé.</li>';
    }
}

/**
 * Fetches drugs from Supabase and IndexedDB, then displays them.
 */
async function fetchAndDisplayDrugs() {
    const stockUl = getElement('stock-ul');
    const drugsUl = getElement('drugs-ul');
    
    // Update loading messages
    if (stockUl) stockUl.innerHTML = '<li class="text-muted">Chargement du stock...</li>';
    if (drugsUl) drugsUl.innerHTML = '<li class="text-muted">Chargement des médicaments...</li>';
    
    let drugs = [];

    try {
        const { data, error } = await supabase.from('drugs').select('*').order('name', { ascending: true });
        if (error) throw error;
        drugs = data;
        // Use bulkPut to handle updates
        await db.drugs.bulkPut(drugs);
    } catch (networkError) {
        drugs = await db.drugs.toArray();
        if (drugs.length > 0) showStatus('Mode hors ligne avec données en cache.', false);
    }

    // Stock is a FOLD of the ledger, never a stored balance (spec §4.1, invariant
    // 1). `drugs.current_stock` is dropped by migration 0008 — reading it here
    // would show every position as `undefined`, and the read-modify-write race that
    // conjured three units out of nothing (audit C2) is gone with the column.
    const { positions } = foldStock(await allLedgerEvents());

    // Update Current Stock list
    if (stockUl) {
        stockUl.innerHTML = '';
        if (drugs.length > 0) {
            drugs.forEach(drug => {
                const position = positions[drug.id] || 0;
                const liStock = document.createElement('li');
                const stockColor = position <= 0
                    ? 'text-red-500'
                    : position < LOW_STOCK_THRESHOLD ? 'text-orange-500' : 'text-gray-600';
                liStock.className = `flex justify-between items-center py-1 font-medium ${stockColor}`;
                const name = document.createElement('span');
                name.textContent = drug.name;
                const qty = document.createElement('span');
                // A NEGATIVE position is shown, never clamped: an offline oversell
                // is a fact about the world (spec §4) and hiding it is how stock
                // goes missing quietly.
                qty.textContent = `${position} ${drug.unit || ''}`.trim();
                liStock.appendChild(name);
                liStock.appendChild(qty);
                stockUl.appendChild(liStock);
            });
        } else {
            stockUl.innerHTML = '<li class="text-muted">Aucune donnée de stock trouvée.</li>';
        }
    }

    // Update Available Drugs list
    if (drugsUl) {
        drugsUl.innerHTML = '';
        if (drugs.length > 0) {
            drugs.forEach(drug => {
                const liDrug = document.createElement('li');
                liDrug.className = 'flex justify-between items-center py-1 text-gray-600';
                const name = document.createElement('span');
                name.textContent = drug.name;
                const price = document.createElement('span');
                price.className = 'font-semibold text-green-600';
                price.textContent = formatCurrency(drug.price);
                liDrug.appendChild(name);
                liDrug.appendChild(price);
                drugsUl.appendChild(liDrug);
            });
        } else {
            drugsUl.innerHTML = '<li class="text-muted">Aucun médicament trouvé.</li>';
        }
    }
}


/**
 * Fetches and displays all relevant data for the Admin dashboard.
 */
async function fetchAndDisplayAllData() {
    showStatus('Chargement des données...', false);
    await Promise.all([
        fetchAndDisplayRevenueTypes(),
        fetchAndDisplayDrugs(),
        loadSettingsPageStats()
    ]);
    showStatus('Données chargées avec succès.', false);
}

/**
 * Loads statistics for the Settings page cards
 */
async function loadSettingsPageStats() {
    /** Write a count into a tile, or leave the tile alone if it is not on the page. */
    const paint = (id, value) => {
        const el = document.getElementById(id);
        if (el) el.textContent = String(value);
    };

    try {
        // Load user counts
        const { data: users, error: usersError } = await supabase
            .from('users')
            .select('role');

        if (!usersError && users) {
            const adminCount = users.filter(u => u.role === 'admin').length;
            const cashierCount = users.filter(u => u.role === 'cashier_a' || u.role === 'cashier_b').length;

            paint('users-admin-count', adminCount);
            paint('users-cashier-count', cashierCount);
            // The quick-stats strip at the top of Paramètres was never wired to
            // anything: four tiles reading `0` on a hospital with real users,
            // real services and real drugs on file.
            paint('settings-users-count', users.length);
        }

        // Load services count. `category` comes along because the « Catégories »
        // tile was a hard-coded `5` — a number that was wrong the moment anyone
        // added or removed a category, and that nobody would ever notice.
        const { data: services, error: servicesError } = await supabase
            .from('revenue_types')
            .select('id, category, is_active');

        if (!servicesError && services) {
            // ACTIVE ones, in both tiles. Migration 0009 made removal a soft
            // delete (`is_active = false`) so a price list item named on a ledger
            // line can never be deleted out from under it — which means the raw
            // row count now includes services the hospital no longer offers, and
            // « Services : 112 » would be counting history.
            const active = services.filter(s => s.is_active !== false);
            paint('services-active-count', active.length);
            paint('settings-services-count', active.length);
            paint('services-categories-count', new Set(
                active.map(s => (typeof s.category === 'string' && s.category.trim()) || 'Sans catégorie')
            ).size);
        }

        // The catalogue count. Read from the server, falling back to the local
        // cache — `fetchAndDisplayDrugs` runs CONCURRENTLY with this function, so
        // counting the cache alone would report 0 on a first run and race on
        // every other one.
        try {
            const { data: drugRows, error: drugsError } = await supabase
                .from('drugs').select('id').eq('is_active', true);
            if (drugsError) throw drugsError;
            paint('settings-drugs-count', (drugRows || []).length);
        } catch {
            try {
                const cached = await db.drugs.toArray();
                paint('settings-drugs-count', cached.filter(d => d.is_active !== false).length);
            } catch { /* cache only */ }
        }

        // « Types de rapports » was hard-coded to 5 and there are now eight tiles.
        // Counting the buttons keeps the claim true by construction: add a report,
        // the number moves.
        paint('reports-types-count', document.querySelectorAll('.report-type-btn').length);

        // Drug and receipt stats come from the LEDGER FOLD, not from
        // `drugs.current_stock` (dropped by 0008) and not from `transactions`
        // (renamed to `transactions_legacy_20260727`). Same events, same folds as
        // the dashboard — audit H2 was three screens answering one question three
        // ways because each ran its own query.
        const events = await allLedgerEvents();
        const { positions } = foldStock(events);
        const stockValues = Object.values(positions);

        const inStockEl = document.getElementById('drugs-in-stock-count');
        const lowStockEl = document.getElementById('drugs-low-stock-count');
        if (inStockEl) inStockEl.textContent = String(stockValues.filter(v => v > 0).length);
        if (lowStockEl) {
            lowStockEl.textContent = String(
                stockValues.filter(v => v > 0 && v < LOW_STOCK_THRESHOLD).length
            );
        }

        const today = todayBusinessDate();
        const monthPrefix = today.slice(0, 7);
        const isSale = e => e && e.kind === 'sale';

        const todayCountEl = document.getElementById('receipts-today-count');
        if (todayCountEl) {
            todayCountEl.textContent = String(
                events.filter(e => isSale(e) && e.business_date === today).length
            );
        }

        const monthReceipts = events
            .filter(e => isSale(e) && String(e.business_date || '').startsWith(monthPrefix)).length;
        paint('receipts-month-count', monthReceipts);
        paint('settings-receipts-count', monthReceipts);

    } catch (error) {
        console.error('Error loading settings stats:', error);
    }
}

/**
 * Fetches the user's role from JWT metadata first, then falls back to public.users table.
 * @param {object} session - The Supabase session object.
 * @returns {Promise<string|null>} The user's role or null if not found/error.
 */
async function getUserRole(session) {
    try {
        if (session?.user?.app_metadata?.role) {
            return session.user.app_metadata.role;
        }
        const { data, error } = await supabase.from('users').select('role').eq('id', session.user.id).single();
        if (error) {
            console.error('Error fetching user role from table:', error.message);
            return null;
        }
        return data ? data.role : null;
    } catch (e) {
        console.error('Exception while fetching user role:', e.message);
        return null;
    }
}

/**
 * Initialize modules based on user role (with duplicate prevention)
 */
async function initializeModulesForRole(userRole) {
    // `DayClose` is in ALL THREE lists on purpose: the drawer count is the CASHIER's
    // ceremony, and only its `v_close_check` half is admin-only (the module hides that
    // section for a non-admin). `ConflictQueue` and `DeviceProvisioning` are admin-only
    // because `resolve_conflict` and `register_device` are admin-only server-side —
    // offering a button that the server is guaranteed to refuse is not a feature.
    const modulesToInit = {
        admin: ['UserManagement', 'RevenueTypesManagement', 'DrugsManagement', 'ReceiptManagement', 'Reporting', 'CashierATransactions', 'CashierBTransactions', 'DeviceProvisioning', 'ConflictQueue', 'DayClose'],
        cashier_a: ['CashierATransactions', 'DayClose'],
        cashier_b: ['CashierBTransactions', 'DayClose']
    };

    const moduleMap = {
        UserManagement, RevenueTypesManagement, DrugsManagement, ReceiptManagement,
        Reporting, CashierATransactions, CashierBTransactions,
        DeviceProvisioning, ConflictQueue, DayClose
    };

    const roleModules = modulesToInit[userRole] || [];
    for (const modName of roleModules) {
        if (!AppState.modulesInitialized[modName]) {
            moduleMap[modName].init();
            AppState.modulesInitialized[modName] = true;
        }
    }
}

/**
 * Updates the UI based on the user's authentication session and role.
 * @param {object|null} session - The Supabase session object.
 */
async function updateAuthUI(session) {
    if (!AppState.initialized) {
        return;
    }

    const authSection = getElement('auth-section');
    const mainApp = getElement('main-app');
    const sidebar = getElement('sidebar');

    if (session) {
        const userRole = await getUserRole(session);
        if (userRole) {
            AppState.currentUser = session.user;
            AppState.currentRole = userRole;

            // Boot the money engine BEFORE the cashier modules initialise, so the
            // collection gate they consult already knows about durability, device
            // identity and receipt supply. Idempotent — a token refresh re-enters
            // here and gets the same engine back.
            await SyncService.init({ role: userRole });

            authSection.classList.add('hidden');
            mainApp.classList.remove('hidden');
            sidebar.classList.remove('hidden');

            // Delegate core UI updates to ui.js
            if (window.UI) {
                UI.onUserLogin(session.user.email, userRole);
            }

            // --- PRECISE NAVIGATION CONTROL ---
            const navLinks = document.querySelectorAll('.sidebar-link[data-target]');
            const adminDivider = document.querySelector('.sidebar-divider');
            const generalSection = document.querySelector('.sidebar-section:last-of-type');

            // La MÊME table que la restauration du hash (UI.panesForRole) : un
            // panneau accessible par hash mais absent du menu, ou l'inverse,
            // est un écart qui ne se verrait qu'en production.
            const allowedPanes = window.UI ? UI.panesForRole(userRole) : [];
            navLinks.forEach(link => {
                const target = link.dataset.target;
                let showLink = allowedPanes.includes(target);

                // Hide the main "Administration" section title for non-admins
                if (target === 'admin-dashboard' && userRole !== 'admin') {
                    showLink = false;
                }

                // Get the parent menu item
                const menuItem = link.closest('.sidebar-menu-item');
                if (menuItem) {
                    menuItem.style.display = showLink ? 'block' : 'none';
                } else {
                    link.style.display = showLink ? 'flex' : 'none';
                }
            });

            // Show/hide admin divider and section for non-admins
            if(adminDivider) {
                adminDivider.style.display = userRole === 'admin' ? 'block' : 'none';
            }
            // --- END OF NAVIGATION CONTROL ---

            await initializeModulesForRole(userRole);
            
            switch (userRole) {
                case 'admin': await fetchAndDisplayAllData(); break;
                case 'cashier_a': await fetchAndDisplayRevenueTypes(); break;
                case 'cashier_b': await fetchAndDisplayDrugs(); break;
            }

            if (userRole === 'admin' && window.AppCore) {
                const stats = await window.AppCore.getDashboardStats();
                window.AppCore.renderDashboardStats(stats);
            }

        } else {
            showStatus('Rôle utilisateur non trouvé. Veuillez contacter l\'administrateur.', true);
            await supabase.auth.signOut();
        }
    } else {
        // User is logged out. The engine stops; the LEDGER AND OUTBOX SURVIVE —
        // `db/logout.js` clears only the reference-data caches, and there is no
        // path here that drops a table.
        shutdownEngine();
        SyncService.stop();
        // The conflict count is published on a timer and on engine status changes; both
        // must stop with the session, or a signed-out screen keeps re-reading tables.
        ConflictQueue.stop();
        AppState.currentUser = null;
        AppState.currentRole = null;
        Object.keys(AppState.modulesInitialized).forEach(key => AppState.modulesInitialized[key] = false);

        authSection.classList.remove('hidden');
        mainApp.classList.add('hidden');
        sidebar.classList.add('hidden');

        if (window.UI) UI.onUserLogout();
    }
}


/**
 * Sets up realtime subscriptions after app is initialized
 */
function setupRealtimeSubscriptions() {
    const revenueTypesChannel = supabase.channel('public:revenue_types').on('postgres_changes', { event: '*', schema: 'public', table: 'revenue_types' }, payload => {
        console.log('Realtime change on revenue_types:', payload);
        if (AppState.currentRole === 'admin' || AppState.currentRole === 'cashier_a') {
            fetchAndDisplayRevenueTypes();
        }
    }).subscribe();
    AppState.realtimeChannels.push(revenueTypesChannel);

    const drugsChannel = supabase.channel('public:drugs').on('postgres_changes', { event: '*', schema: 'public', table: 'drugs' }, payload => {
        console.log('Realtime change on drugs:', payload);
        if (AppState.currentRole === 'admin' || AppState.currentRole === 'cashier_b') {
            fetchAndDisplayDrugs();
        }
    }).subscribe();
    AppState.realtimeChannels.push(drugsChannel);
}

/**
 * Main initialization function
 */
async function initializeApp() {
    const elementIds = [
        'auth-section', 'main-app', 'login-form', 'email', 'password',
        'logout-button', 'install-button', 'sidebar', 'revenue-types-ul', 'stock-ul', 'drugs-ul'
    ];
    elementIds.forEach(id => AppState.elements[id] = document.getElementById(id));

    AppState.initialized = true;
    console.log('App Core Initialized.');

    setupFormHandlers();
    setupPWAHandlers();
    setupRealtimeSubscriptions();

    // The engine is booted from `updateAuthUI`, not here: registering a device and
    // allocating a receipt block are authenticated RPCs, so they need a session.
    const { data: { session } } = await supabase.auth.getSession();
    await updateAuthUI(session);

    supabase.auth.onAuthStateChange(async (event, session) => {
        if (['SIGNED_IN', 'SIGNED_OUT', 'USER_UPDATED', 'TOKEN_REFRESHED'].includes(event)) {
            await updateAuthUI(session);
        }
    });

    // ---- The idle-tablet fix ------------------------------------------------
    // The JWT expires after one hour and its renewal runs on setInterval — which
    // browsers THROTTLE OR SUSPEND while a tab is hidden or the machine sleeps.
    // A tablet left idle therefore came back with a dead token and a refresher
    // that never fired, and every later query spun forever ("tout charge
    // indéfiniment jusqu'au hard refresh"). On wake we restart the refresher
    // cleanly and force one getSession(), which renews an expired token NOW,
    // before any screen fires its first query. The supabase-js upgrade that
    // ships with this change (2.50 → 2.112) also carries a year of fixes to the
    // wake-after-sleep lock path this leans on.
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
            supabase.auth.startAutoRefresh();
            supabase.auth.getSession().catch((error) => {
                console.warn('Réveil : getSession a échoué —', error);
            });
        } else {
            supabase.auth.stopAutoRefresh();
        }
    });
}

/**
 * Log out — and REFUSE while this device still holds money the server has never
 * seen.
 *
 * The old implementation did `await db.delete()` and re-declared the schema
 * (audit C5). Every `sync_status:'pending'` row — cash already taken, receipts
 * already in patients' hands — was wiped with no warning and no pending check,
 * and the cashier was shown « Déconnexion réussie ». Two disasters on one line.
 *
 * `db/logout.js` refuses while the outbox is non-empty and clears ONLY the
 * reference-data caches. There is deliberately no force flag: a "log out anyway"
 * option is exactly how the unsynced-cash path grows back.
 */
async function performLogout() {
    // Ask first, so the cashier learns about the queue BEFORE the button moves.
    let gate;
    try {
        gate = await canLogout(db);
    } catch (error) {
        showStatus('Vérification des ventes en attente impossible — déconnexion annulée.', true);
        console.error('canLogout failed:', error);
        return;
    }

    if (!gate.allowed) {
        // Say the count AND the amount: a number of records means nothing to a
        // cashier; « 42 500 FCFA » does.
        showStatus(
            `Déconnexion impossible : ${gate.pendingCount} vente(s) non synchronisée(s) `
            + `pour ${formatCurrency(gate.pendingTotal)}. Reconnectez-vous au réseau et attendez la synchronisation.`,
            true
        );
        // A nudge, in case the network is back and nobody has kicked a round.
        void SyncService.runSync();
        return;
    }

    showStatus('Déconnexion en cours...', false);
    try {
        await performSafeLogout(db, { signOut: () => supabase.auth.signOut() });
        shutdownEngine();
        SyncService.stop();
        showStatus('Déconnexion réussie.', false);
    } catch (error) {
        showStatus(messageForError(error), true);
        console.error('Logout refused or failed:', error);
    }
}

// Make logout available globally
window.logout = performLogout;

/**
 * Sets up form event handlers
 */
function setupFormHandlers() {
    getElement('login-form')?.addEventListener('submit', async (e) => {
        e.preventDefault();
        const email = getElement('email').value;
        const password = getElement('password').value;
        showStatus('Connexion en cours...', false);
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) showStatus('Échec de connexion: ' + error.message, true);
    });

    // Legacy logout button handler
    getElement('logout-button')?.addEventListener('click', async () => {
        await performLogout();
    });

    // Logout link handler (for new sidebar design)
    document.getElementById('logout-link')?.addEventListener('click', async (e) => {
        e.preventDefault();
        await performLogout();
    });

    // Password toggle functionality
    const togglePassword = document.getElementById('toggle-password');
    const passwordInput = document.getElementById('password');
    if (togglePassword && passwordInput) {
        togglePassword.addEventListener('click', () => {
            const type = passwordInput.getAttribute('type') === 'password' ? 'text' : 'password';
            passwordInput.setAttribute('type', type);

            // Toggle eye icons
            const eyeOpen = togglePassword.querySelector('.eye-open');
            const eyeClosed = togglePassword.querySelector('.eye-closed');
            if (eyeOpen && eyeClosed) {
                eyeOpen.classList.toggle('hidden');
                eyeClosed.classList.toggle('hidden');
            }
        });
    }

    // Load real staff count on login page
    loadStaffCount();
}

/**
 * Loads and displays the real staff count on login page
 */
async function loadStaffCount() {
    const staffCountElement = document.getElementById('staff-count');
    if (!staffCountElement) return;

    try {
        const { count, error } = await supabase
            .from('users')
            .select('*', { count: 'exact', head: true });

        if (!error && count !== null) {
            staffCountElement.textContent = count + '+';
        }
    } catch (err) {
        console.log('Could not load staff count:', err);
    }
}

/**
 * Sets up PWA-related handlers
 */
function setupPWAHandlers() {
    let deferredPrompt;
    const installButton = getElement('install-button');
    // The whole card is toggled, not just the button: a card headed « Télécharger
    // l'App » with no button reads as broken rather than as unavailable.
    const installCard = getElement('install-card') || installButton;

    // The card ships hidden. It used to ship visible while the code below assumed
    // otherwise (`classList.remove('hidden')` on an element that never had the
    // class), so « Installer » was permanently on screen and did nothing whenever
    // `beforeinstallprompt` had not fired — which is every already-installed
    // device, every iOS browser, and every browser that was dismissed once.
    const show = () => installCard?.classList.remove('hidden');
    const hide = () => installCard?.classList.add('hidden');

    window.addEventListener('beforeinstallprompt', (e) => {
        e.preventDefault();
        deferredPrompt = e;
        show();
    });

    // Installed from our button or from the browser's own menu — either way the
    // offer is spent.
    window.addEventListener('appinstalled', () => {
        deferredPrompt = null;
        hide();
    });

    installButton?.addEventListener('click', async () => {
        if (!deferredPrompt) return;
        hide();
        deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        console.log(`User response to the install prompt: ${outcome}`);
        deferredPrompt = null;
    });

    if ('serviceWorker' in navigator) {
        navigator.serviceWorker.register('service-worker.js')
            .then(reg => console.log('Service Worker registered.', reg))
            .catch(err => console.error('Service Worker registration failed:', err));
    }
}

// --- AppCore API for UI integration ---
window.AppCore = {
    getCurrentRole: function() {
        return AppState.currentRole;
    },
    /**
     * Today's figures, folded from the ledger.
     *
     * AUDIT H2, CLOSED. This used to sum `total_amount` over `transactions`
     * INCLUDING voided rows and IGNORING discounts, while `getWeeklyRevenueData`
     * and `reporting.js` excluded and subtracted correctly — so voiding a 25,000
     * receipt made the answer depend on which tile you read. Both now call
     * `foldMoney`, the one place cash is summed.
     *
     * AUDIT H6, CLOSED. Queued offline money is in the total, because the local
     * mirror row exists the moment the cashier confirms. The caveat string
     * « {n} vente(s) non encore synchronisée(s) sont incluses dans ce montant »
     * (spec §9) ships WITH the figure, so the number is honest about what it is.
     *
     * Corrections are attributed to their OWN business_date here — this is the
     * cash-position view, and spec §10.1 says `v_daily_cash` books a correction on
     * the day it was made. (The day-close fold attributes to the TARGET sale's
     * device-day; two views, two correct-but-different attributions.)
     */
    getDashboardStats: async function() {
        try {
            const events = await allLedgerEvents();
            const today = todayBusinessDate();
            const monthPrefix = today.slice(0, 7);

            const todayFold = foldMoney(events.filter(e => e && e.business_date === today));
            const regie = todayFold.byChannel.regie || { net: 0 };
            const pharmacy = todayFold.byChannel.pharmacy || { net: 0 };

            const monthFold = foldMoney(
                events.filter(e => e && String(e.business_date || '').startsWith(monthPrefix))
            );
            const totalTransactions = Object.values(monthFold.byChannel)
                .reduce((sum, bucket) => sum + bucket.saleCount, 0);

            const { positions } = foldStock(events);
            const lowStockItems = Object.values(positions)
                .filter(v => v < LOW_STOCK_THRESHOLD).length;

            const pending = await pendingSummary(db);

            // The comparison periods, folded from the SAME events with the SAME
            // function. The tiles used to carry a hard-coded « Augmenté depuis
            // hier » and an up-arrow that never pointed anywhere else — a claim
            // about yesterday made without reading yesterday.
            const yesterday = shiftBusinessDate(today, -1);
            const yesterdayFold = foldMoney(events.filter(e => e && e.business_date === yesterday));
            const yesterdayRegie = yesterdayFold.byChannel.regie || { net: 0 };
            const yesterdayPharmacy = yesterdayFold.byChannel.pharmacy || { net: 0 };

            const lastMonthPrefix = shiftBusinessDate(startOfMonth(today), -1).slice(0, 7);
            const lastMonthFold = foldMoney(
                events.filter(e => e && String(e.business_date || '').startsWith(lastMonthPrefix))
            );
            const lastMonthTransactions = Object.values(lastMonthFold.byChannel)
                .reduce((sum, bucket) => sum + bucket.saleCount, 0);

            return {
                todayRevenue: regie.net,
                pharmacySales: pharmacy.net,
                totalTransactions,
                lowStockItems,
                // Carried alongside the figures so a tile can render the caveat.
                pendingCount: pending.count,
                pendingTotal: pending.total,
                pendingNotice: pendingInTotalNotice(pending.count),
                // KEYED BY `data-stat`, IN KEBAB-CASE, NOT by the camelCase field
                // names above. `updateStatTrends` finds each tile with
                // `[data-stat="…"]`, so a camelCase key here silently matches
                // nothing: `querySelector` returns null, the function returns
                // early, and all four pills stay hidden forever with no error
                // anywhere. That shipped, and the tiles looked merely unfinished
                // rather than broken — which is why it survived a green test run.
                trends: {
                    'today-revenue': trendBetween(regie.net, yesterdayRegie.net, formatCurrency, 'depuis hier'),
                    'pharmacy-sales': trendBetween(pharmacy.net, yesterdayPharmacy.net, formatCurrency, 'depuis hier'),
                    'total-transactions': trendBetween(
                        totalTransactions, lastMonthTransactions,
                        n => `${n} vente(s)`, 'sur le mois dernier'),
                    // Stock has no yesterday to compare against — the fold gives a
                    // position, not a history. The tile states its threshold, which
                    // is the fact that makes the count mean anything.
                    'low-stock-items': {
                        direction: lowStockItems > 0 ? 'warning' : 'flat',
                        change: 0,
                        label: lowStockItems > 0
                            ? `Sous le seuil de ${LOW_STOCK_THRESHOLD} unités`
                            : `Tout au-dessus du seuil de ${LOW_STOCK_THRESHOLD} unités`
                    }
                }
            };
        } catch (error) {
            console.error('Error folding dashboard stats:', error);
            return {
                todayRevenue: 0, pharmacySales: 0, totalTransactions: 0, lowStockItems: 0,
                pendingCount: 0, pendingTotal: 0, pendingNotice: '', trends: null
            };
        }
    },

    /**
     * Net revenue per weekday for the current week, folded from the same events as
     * the tiles above — so the chart and the dashboard cannot disagree (audit H2).
     *
     * The week is built from BUSINESS dates (WAT, fixed +01:00), not from
     * `new Date().setHours(0,0,0,0)`, so no sale falls between two bounds and out
     * of every bucket (audit H1).
     */
    getWeeklyRevenueData: async function() {
        // Delegates to `getRevenueSeries`, which grew out of this function when
        // the « Mois » button had to work. Two hand-rolled week builders would be
        // two chances to disagree about which day a sale belongs to — the exact
        // shape of audit H1 — so there is one, and this name still answers.
        const series = await this.getRevenueSeries('week');
        return series.values;
    },

    /**
     * Net revenue per bucket for the requested period, with the labels that name
     * the buckets — so the « Semaine » / « Mois » selector changes the QUESTION,
     * not just the numbers. `updateChartPeriod('month')` used to log
     * "Monthly view not yet implemented" and leave the week's bars on screen
     * under a highlighted « Mois » button.
     *
     * Same fold, same events, same business dates as every other figure.
     * @param {'week'|'month'} [period]
     * @returns {Promise<{labels:string[], values:number[], dates:string[], caption:string}>}
     */
    getRevenueSeries: async function(period = 'week', options = {}) {
        const WEEKDAYS = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
        const empty = { labels: [], dates: [], values: [], caption: '', granularity: 'day', error: null };
        try {
            const today = todayBusinessDate();

            /** @type {{label:string, dates:string[]}[]} */
            let buckets;
            let caption;
            let granularity = 'day';

            if (period === 'custom') {
                const from = options && options.from;
                const to = options && options.to;
                // VALIDATED HERE, not in the click handler. A bound that reaches the
                // fold malformed produces a chart of nothing that looks exactly like
                // a quiet month, which is the one confusion this dashboard exists to
                // prevent.
                if (!isBusinessDateString(from) || !isBusinessDateString(to)) {
                    return { ...empty, error: 'Renseignez deux dates valides (JJ/MM/AAAA).' };
                }
                if (from > to) {
                    return { ...empty, error: 'La date de début est postérieure à la date de fin.' };
                }
                if (daysInclusive(from, to) > MAX_RANGE_DAYS) {
                    return { ...empty, error: `Période trop longue : ${MAX_RANGE_DAYS} jours au maximum.` };
                }
                const built = bucketsForRange(from, to);
                buckets = built.buckets;
                granularity = built.granularity;
                caption = `Recettes nettes par ${GRANULARITY_NOUN[granularity]} — `
                    + `du ${frenchDate(from)} au ${frenchDate(to)}`;

            } else if (period === 'year') {
                const year = today.slice(0, 4);
                granularity = 'month';
                buckets = MONTH_LABELS.map((label, i) => {
                    const first = `${year}-${String(i + 1).padStart(2, '0')}-01`;
                    return {
                        label,
                        dates: Array.from({ length: daysInMonth(first) }, (_, d) => shiftBusinessDate(first, d))
                    };
                });
                caption = `Recettes nettes par mois — année ${year}`;

            } else if (period === 'month') {
                const first = startOfMonth(today);
                buckets = Array.from({ length: daysInMonth(today) }, (_, i) => {
                    const date = shiftBusinessDate(first, i);
                    return { label: String(Number(date.slice(8, 10))), dates: [date] };
                });
                caption = `Recettes nettes par jour — ${first.slice(0, 7)}`;

            } else {
                const startOfWeek = shiftBusinessDate(today, -weekdayOf(today));
                buckets = Array.from({ length: 7 }, (_, i) => ({
                    label: WEEKDAYS[i],
                    dates: [shiftBusinessDate(startOfWeek, i)]
                }));
                caption = 'Recettes nettes par jour — semaine en cours';
            }

            // ONE PASS over the ledger regardless of how many buckets were asked
            // for: a ten-year monthly view must not walk the events 120 times.
            const events = await allLedgerEvents();
            const everyDate = buckets.flatMap(b => b.dates);
            const byDate = bucketByDate(events, everyDate);

            // The WHOLE period, folded once alongside the buckets.
            //
            // A net series cannot distinguish « rien ne s'est passé » from « il
            // s'est passé quelque chose qui s'annule » — a 5 000 encaissé, annulé
            // de 4 000 et remisé de 1 000 draws exactly the same flat line as an
            // empty week. Those are opposite facts for anyone holding a drawer,
            // so the gross and the corrections are carried out with the series and
            // the caption says which of the two it is looking at.
            const periodFold = foldMoney(everyDate.flatMap(d => byDate.get(d) || []));
            const saleCount = Object.values(periodFold.byChannel)
                .reduce((sum, bucket) => sum + bucket.saleCount, 0);

            return {
                labels: buckets.map(b => b.label),
                dates: everyDate,
                values: buckets.map(b => foldMoney(b.dates.flatMap(d => byDate.get(d) || [])).net),
                caption,
                granularity,
                totals: {
                    gross: periodFold.gross,
                    voided: periodFold.voided,
                    discounted: periodFold.discounted,
                    net: periodFold.net,
                    saleCount
                },
                error: null
            };
        } catch (error) {
            console.error('Error folding revenue series:', error);
            return {
                ...empty,
                labels: WEEKDAYS.slice(),
                values: [0, 0, 0, 0, 0, 0, 0],
                error: 'Lecture du registre impossible.'
            };
        }
    },

    /**
     * The last few ledger events, newest first, for « Transactions Récentes ».
     *
     * This list was a HARD-CODED ROW in `index.html` — « Consultation ·
     * Aujourd'hui, 09:15 · 5 000 XAF » — that no code ever touched. On a hospital
     * screen showing a day with no sales, it read as five thousand francs
     * collected. Nothing here is invented: an empty ledger returns an empty array
     * and the pane says so.
     *
     * Corrections are INCLUDED, signed. A void that vanished from the feed would
     * leave the sale it cancels as the last word on screen.
     * @param {number} [limit]
     * @returns {Promise<object[]>}
     */
    getRecentActivity: async function(limit = 6) {
        try {
            const events = await allLedgerEvents();

            // Built over ALL events, not just the slice being shown: a void in the
            // feed usually cancels a sale that has already scrolled out of it, and
            // a description resolved only within the window would be missing for
            // exactly the corrections a reader most needs explained.
            const describedSale = new Map();
            for (const event of events) {
                if (event && event.kind === 'sale' && event.id != null) {
                    const described = describeItems(event);
                    if (described) describedSale.set(event.id, described);
                }
            }

            return events
                .filter(e => e && typeof e === 'object' && e.id != null && typeof e.kind === 'string')
                .sort((a, b) => eventInstant(b) - eventInstant(a))
                .slice(0, Math.max(0, limit))
                .map(event => toActivityRow(event, describedSale));
        } catch (error) {
            console.error('Error reading recent activity:', error);
            return [];
        }
    },

    /**
     * Who collected what today — the real staff list, from `public.users`, with
     * each person's net take folded from the ledger.
     *
     * « Caissier A / Caissier B · Actif » was written into the HTML by hand and
     * stayed lit whether or not either of them had opened the app.
     *
     * A correction is attributed to the collector of the SALE it cancels, the way
     * `reporting.js` does it (§10.1) — a void authored by an admin must reduce the
     * cashier's line, not the admin's. Every event lands in exactly one bucket, so
     * the members plus `unattributed` sum to the day's net.
     *
     * `unattributed` is not a rounding bucket: `collected_by` is stamped
     * SERVER-SIDE, so a sale still sitting in the outbox genuinely has no
     * collector on this device yet. Saying so beats silently crediting whoever
     * happens to be logged in.
     * @returns {Promise<{members:object[], unattributed:{count:number,total:number}, dayNet:number}>}
     */
    getStaffActivity: async function() {
        let users = [];
        try {
            const { data, error } = await supabase
                .from('users').select('id, email, role').order('email', { ascending: true });
            if (error) throw error;
            users = data || [];
            // Cache it: the dashboard must still name people while offline.
            try { await db.users.bulkPut(users); } catch { /* cache only */ }
        } catch (networkError) {
            try { users = await db.users.toArray(); } catch { users = []; }
        }

        try {
            const events = await allLedgerEvents();
            const today = todayBusinessDate();
            const dayEvents = events.filter(e => e && e.business_date === today);

            const collectorOfSale = new Map();
            for (const event of dayEvents) {
                if (event.kind === 'sale' && event.id != null) {
                    collectorOfSale.set(event.id, event.collected_by || null);
                }
            }

            /** @type {Map<string|null, object[]>} */
            const buckets = new Map();
            for (const event of dayEvents) {
                const key = event.kind === 'sale'
                    ? (event.collected_by || null)
                    : (collectorOfSale.has(event.reverses_id)
                        ? collectorOfSale.get(event.reverses_id)
                        : (event.collected_by || null));
                if (!buckets.has(key)) buckets.set(key, []);
                buckets.get(key).push(event);
            }

            const foldFor = key => foldMoney(buckets.get(key) || []);
            const members = users.map(user => {
                const fold = foldFor(user.id);
                const saleCount = Object.values(fold.byChannel)
                    .reduce((sum, bucket) => sum + bucket.saleCount, 0);
                const email = typeof user.email === 'string' ? user.email : '';
                const name = email.split('@')[0].replace(/[._]/g, ' ').trim() || email || 'Utilisateur';
                return {
                    id: user.id,
                    email,
                    name: name.charAt(0).toUpperCase() + name.slice(1),
                    initials: (email.slice(0, 2) || '??').toUpperCase(),
                    role: ROLE_LABELS[user.role] || user.role || 'Rôle inconnu',
                    saleCount,
                    net: fold.net,
                    active: saleCount > 0
                };
            }).sort((a, b) => b.net - a.net || a.name.localeCompare(b.name, 'fr'));

            const orphan = foldFor(null);
            const orphanCount = Object.values(orphan.byChannel)
                .reduce((sum, bucket) => sum + bucket.saleCount, 0);

            return {
                members,
                unattributed: { count: orphanCount, total: orphan.net },
                dayNet: foldMoney(dayEvents).net
            };
        } catch (error) {
            console.error('Error folding staff activity:', error);
            return { members: [], unattributed: { count: 0, total: 0 }, dayNet: 0 };
        }
    },

    /**
     * Gross revenue per article over a trailing window, for the analytics donut.
     *
     * That chart shipped with `[30, 25, 20, 15, 10]` over five invented category
     * names. It was the most convincing thing on the page and none of it was true.
     *
     * Voided sales are excluded, and the voided set is read from ALL events rather
     * than from the window — a sale made inside the window can be cancelled
     * outside it, and filtering the correction away would leave the cancelled
     * revenue standing (spec §8).
     *
     * A DISCOUNT IS NOT SUBTRACTED HERE, for the reason `renderRevenueByService`
     * gives: a discount reduces a sale, not any one article, and spreading it
     * across lines would invent per-article revenue nobody ever charged. The
     * caption says so, so the total is not mistaken for net.
     * @param {number} [days] Trailing window, in business days.
     * @param {number} [top] Slices before the remainder is grouped as « Autres ».
     * @returns {Promise<{labels:string[], values:number[], total:number, caption:string}>}
     */
    getCategoryBreakdown: async function(days = 30, top = 5) {
        const empty = { labels: [], values: [], total: 0, caption: '' };
        try {
            const events = await allLedgerEvents();
            const today = todayBusinessDate();
            const window = new Set(
                Array.from({ length: days }, (_, i) => shiftBusinessDate(today, i - (days - 1)))
            );

            const voidedIds = new Set(
                events.filter(e => e && e.kind === 'void' && e.reverses_id).map(e => e.reverses_id)
            );

            /** @type {Map<string, number>} */
            const byItem = new Map();
            const add = (name, amount) => {
                if (!Number.isFinite(amount) || amount <= 0) return;
                byItem.set(name, (byItem.get(name) || 0) + Math.round(amount));
            };

            for (const event of events) {
                if (!event || event.kind !== 'sale') continue;
                if (!window.has(event.business_date)) continue;
                if (voidedIds.has(event.id)) continue;

                const lines = Array.isArray(event.items) ? event.items : [];
                if (lines.length === 0) {
                    // A sale with no itemised lines still took cash. It goes into a
                    // named bucket rather than out of the chart — a slice that
                    // silently disappears makes the remaining ones look bigger.
                    add('Vente sans ligne détaillée', Number.isInteger(event.amount) ? event.amount : 0);
                    continue;
                }
                for (const line of lines) {
                    if (!line || typeof line !== 'object') continue;
                    const total = Number.isFinite(Number(line.line_total))
                        ? Number(line.line_total)
                        : Number(line.qty) * Number(line.unit_price);
                    add((typeof line.name === 'string' && line.name.trim()) || 'Article non nommé', total);
                }
            }

            const sorted = [...byItem.entries()].sort((a, b) => b[1] - a[1]);
            const head = sorted.slice(0, Math.max(1, top));
            const tail = sorted.slice(Math.max(1, top));
            const labels = head.map(([name]) => name);
            const values = head.map(([, value]) => value);
            if (tail.length > 0) {
                labels.push(`Autres (${tail.length})`);
                values.push(tail.reduce((sum, [, value]) => sum + value, 0));
            }

            return {
                labels,
                values,
                total: values.reduce((sum, v) => sum + v, 0),
                caption: `Recettes brutes par article sur ${days} jours. `
                    + 'Ventes annulées exclues ; les remises portent sur la vente entière '
                    + 'et ne sont pas réparties par article.'
            };
        } catch (error) {
            console.error('Error folding category breakdown:', error);
            return empty;
        }
    },

    /**
     * Net revenue per day over a trailing window, for the 30-day trend line — a
     * chart that until now was built with `Array(30).fill(0)` and never fed again.
     * @param {number} [days]
     * @returns {Promise<{labels:string[], values:number[], dates:string[]}>}
     */
    getRevenueTrend: async function(days = 30) {
        try {
            const events = await allLedgerEvents();
            const today = todayBusinessDate();
            const dates = Array.from(
                { length: days }, (_, i) => shiftBusinessDate(today, i - (days - 1)));
            const byDate = bucketByDate(events, dates);
            return {
                dates,
                labels: dates.map(d => `${d.slice(8, 10)}/${d.slice(5, 7)}`),
                values: dates.map(date => foldMoney(byDate.get(date)).net)
            };
        } catch (error) {
            console.error('Error folding revenue trend:', error);
            return { dates: [], labels: [], values: [] };
        }
    },

    /**
     * The « Rappels » card, derived instead of decorated.
     *
     * It used to read « Vérification de Stock — Heure : 10h00 - 11h00 », a fixed
     * appointment with nothing behind it. Each item below is a condition that is
     * actually true right now and that someone can act on; when none of them are,
     * the card says nothing is outstanding rather than inventing an errand.
     * @returns {Promise<{items:object[]}>}
     */
    getDashboardReminders: async function() {
        const items = [];
        try {
            const events = await allLedgerEvents();
            const { positions } = foldStock(events);
            const low = Object.entries(positions).filter(([, v]) => v < LOW_STOCK_THRESHOLD);
            const negative = low.filter(([, v]) => v < 0);

            if (negative.length > 0) {
                items.push({
                    severity: 'danger',
                    title: `${negative.length} position(s) de stock négative(s)`,
                    detail: 'Vendu au-delà du stock connu — à corriger par un ajustement, pas à masquer.',
                    target: 'cashier-b-dashboard',
                    action: 'Ouvrir la pharmacie'
                });
            }
            if (low.length > 0) {
                items.push({
                    severity: 'warning',
                    title: `${low.length} médicament(s) sous le seuil`,
                    detail: `Moins de ${LOW_STOCK_THRESHOLD} unités en stock.`,
                    target: 'cashier-b-dashboard',
                    action: 'Vérifier le stock'
                });
            }

            const pending = await pendingSummary(db);
            if (pending.count > 0) {
                items.push({
                    severity: 'warning',
                    title: `${pending.count} vente(s) non synchronisée(s)`,
                    detail: `${formatCurrency(pending.total)} déjà encaissé(s) et pas encore envoyé(s). `
                        + 'Ce montant est inclus dans les totaux du jour.',
                    target: null,
                    action: null
                });
            }

            let openConflicts = 0;
            try {
                openConflicts = await db.table('conflicts').where('status').equals('open').count();
            } catch { /* the table may not exist before the first sync */ }
            if (openConflicts > 0) {
                items.push({
                    severity: 'danger',
                    title: `${openConflicts} encaissement(s) refusé(s)`,
                    detail: 'Ils attendent une décision humaine. Tant qu\'ils y sont, cet argent '
                        + 'n\'apparaît dans aucun total.',
                    target: 'admin-dashboard',
                    action: 'Ouvrir la file'
                });
            }
        } catch (error) {
            console.error('Error building reminders:', error);
        }
        return { items };
    },

    /**
     * Paint the dashboard tiles AND the §9 caveat that goes with them.
     *
     * The caveat is not optional decoration: without it « Recettes du jour »
     * silently mixes money the server has and money it has not, and a total whose
     * meaning is ambiguous is a total nobody can reconcile against a drawer.
     * @param {object} stats From `getDashboardStats()`.
     */
    renderDashboardStats: function(stats) {
        if (!stats) return;
        if (window.UI && window.UI.updateDashboardStats) {
            window.UI.updateDashboardStats(stats);
        }
        const notice = document.getElementById('dashboard-pending-notice');
        if (notice) {
            const text = stats.pendingNotice || '';
            notice.textContent = text;
            notice.classList.toggle('hidden', text === '');
        }
    },

    /** The engine handle, for screens that need the transport or the device id. */
    getMoneyEngine: getEngine,
    refreshRevenueTypes: fetchAndDisplayRevenueTypes,
    refreshDrugs: fetchAndDisplayDrugs,
    refreshAllData: fetchAndDisplayAllData
};

// --- Main DOMContentLoaded Listener ---
document.addEventListener('DOMContentLoaded', () => {
    console.log('DOM Content Loaded - Initializing app...');
    initializeApp();
});

// Export functions for external use by other modules
window.fetchAndDisplayRevenueTypes = fetchAndDisplayRevenueTypes;
window.fetchAndDisplayDrugs = fetchAndDisplayDrugs;
